import { markFetchNetworkFailure } from './offline'
import { NOT_SAMPLED_HEADERS, serializeBaggage } from './baggage'
import { nullTel } from './tel'
import type { ContextPropagation, Tel } from './tel-types'

interface InstrumentFetchBaseOpts {
  mode: ContextPropagation
  fetch?: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>
  overwriteHeaders?: boolean
  onResponse?: (response: Response, tel: Tel) => void
  /** Explicit producer projection; the real URL is used only for the request. */
  telemetryUrl?: (url: URL) => { path: string }
}

export type InstrumentFetchOpts = InstrumentFetchBaseOpts &
  (
    | { tel: Tel; getTel?: never }
    | { tel?: never; getTel: () => Tel }
  )

export type InstrumentedFetch = typeof fetch & {
  /** Explicit override for work outside a router-bound resource context. */
  withTel(tel: Tel): typeof fetch
}

export function instrumentFetch(opts: InstrumentFetchOpts): InstrumentedFetch {
  const fetchImpl = opts.fetch ?? fetch

  async function instrumentedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const request = new Request(input, init)
    const parentTel = 'getTel' in opts && opts.getTel ? opts.getTel() : opts.tel
    if (parentTel === nullTel) {
      // :uc[not-sampled-flags-disable-callee-telemetry] flags 00 tell the callee to run under nullTel.
      for (const [key, value] of Object.entries(NOT_SAMPLED_HEADERS[opts.mode])) {
        if (opts.overwriteHeaders || !request.headers.has(key)) request.headers.set(key, value)
      }
      return fetchImpl(request, request.body
        ? ({ duplex: 'half' } as RequestInit & { duplex: 'half' })
        : undefined)
    }
    const url = new URL(request.url)
    const method = request.method
    let path = url.pathname
    let tel: Tel | undefined
    // Optional diagnostics never decide whether the one real request runs.
    try {
      const projected = opts.telemetryUrl?.(url)
      path = projected?.path ?? url.pathname
      tel = parentTel._createTel({
        name: `${method} ${path}`,
        target: 'fetch',
        type: isQueryMethod(method) ? 'query' : 'mutation',
        trace: !parentTel._getBaggage().traceId,
        input: projected ? { method, path } : { method, url: request.url },
      })
      tel.currentSpan.update({
        attributes: {
          'http.method': method,
          'http.request.method': method,
          'url.path': path,
          ...(projected ? {} : { 'url.full': request.url, 'server.address': url.hostname }),
        },
      })
      const headers = serializeBaggage(tel._getBaggage(), { mode: opts.mode })
      for (const [key, value] of Object.entries(headers)) {
        if (opts.overwriteHeaders || !request.headers.has(key)) request.headers.set(key, value)
      }
    } catch {
      try { tel?.currentSpan.end({}) } catch { /* Best effort terminal diagnostics. */ }
      tel = undefined
    }

    let response: Response
    try {
      response = await fetchImpl(request, request.body
        ? ({ duplex: 'half' } as RequestInit & { duplex: 'half' })
        : undefined)
    } catch (error) {
      try {
        // INV-TEL-OFFLINE marks only the TypeError a fetch rejects with: an abort is already expected, and a timeout or
        // an error raised while the browser reports offline is not proof the network is gone.
        if (error instanceof TypeError) markFetchNetworkFailure(error)
        if (isNetworkFailure(error)) {
          const attrs = { operation: `${method} ${path}`, errorName: error instanceof Error ? error.name : 'Error' }
          tel?.warn('Fetch network unavailable', attrs)
          tel?.currentSpan.end({ output: attrs })
        } else tel?.currentSpan.end({ output: {}, error })
      } catch { /* Preserve the original fetch failure. */ }
      throw error
    }
    try {
      if (tel) opts.onResponse?.(response, tel)
      tel?.currentSpan.end({
        output: { status: response.status },
        attributes: {
          'http.status_code': response.status,
          'http.response.status_code': response.status,
        },
      })
    } catch { /* Preserve the exact response without another request. */ }
    return response
  }

  return Object.assign(instrumentedFetch, {
    withTel(tel: Tel): typeof fetch {
      const { getTel: _getTel, ...rest } = opts
      return instrumentFetch({ ...rest, tel })
    },
  }) as InstrumentedFetch
}

function isQueryMethod(method: string): boolean {
  return method === 'GET' || method === 'HEAD' || method === 'OPTIONS'
}

/** HTTP responses are classified separately; this predicate is for thrown fetch failures. */
export function isNetworkFailure(error: unknown): boolean {
  return error instanceof TypeError || error instanceof Error && (error.name === 'AbortError' || error.name === 'TimeoutError') || typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine
}
